import React, { useCallback, useMemo, useState } from "react";

import { Link, Tooltip } from "@mui/material";
import moment from "moment";

import { Account } from "decodable-sdk-js/v1alpha2";
import AlertBanner from "decodable-ui/lib/components/AlertBanner";

import { useActiveAccount } from "../accounts/hooks/useAccounts";
import { timeUnit } from "../util/time";
import { Launch } from "@mui/icons-material";

export type AlertParams = {
  // A key that identifies this alert.
  // Used to store dismissals of the alert in web storage.
  // The value is arbitrary, but should be unique.
  // Recommended format: YYYY-MM-DD-<title>
  key: string;

  // The location specifies where the alert should be shown.
  // If `global` is true, show the alert everywhere.
  // Otherwise, only show the alert when the user's current active
  // account is in one of the listed regions _or_ cells.
  location: {
    global?: boolean;
    regions?: string[];
    cells?: string[];
  };
} & (
  | {
      type: "maintenance";

      // A message to show in addition to the time bounds of the window.
      message: string;

      // The time bounds of the maintenance window.
      // The alert will be shown from 24 hours ahead of the start time,
      // and continue to be shown until the end time.
      time: {
        start: Date;
        end: Date;
      };
    }
  | {
      type: "cta";

      // A message describing the announcement.
      message: string;

      // The label & URL for the CTA link
      cta: {
        label: string;
        url: string;
      };

      // The time bounds of the announcement.
      // The announcement will be shown from the start time to the end time.
      time: {
        start: Date;
        end: Date;
      };
    }
);

const scheduledAlerts: AlertParams[] = [
  //// Example maintenance window alert.
  // {
  //   key: "2024-04-03-dev-demo",
  //   type: "maintenance",
  //   message:
  //     "Stream creation and preview features may be unavailable during the window.",
  //   time: {
  //     start: new Date("2024-04-03T21:00:00.000Z"),
  //     end: new Date("2024-04-03T24:00:00.000Z"),
  //   },
  //   location: {
  //     // All of the dev cells, for PR demonstration purposes.
  //     cells: ["5", "6", "10", "11", "13", "14", "18"],
  //   },
  // },
  {
    key: "2024-04-30-VHOL",
    location: {
      global: true,
    },
    type: "cta",
    message:
      "Hands-on Lab: Set up a real-time ETL pipeline with Gunnar Morling and Hans-Peter Grahsl on July 30th!",
    time: {
      start: new Date("2024-07-19T00:00:00.000Z"),
      end: new Date("2024-07-30T15:00:00.000Z"),
    },
    cta: {
      label: "👉 Register",
      url: "https://info.decodable.co/lab-intro-to-real-time-etl-with-decodable?utm_medium=popup&utm_source=product&utm_term=&utm_content=&utm_campaign=hands-on-lab",
    },
  },
];

export function findActiveAlertForAccount(
  alerts: AlertParams[],
  account: Account,
  dismissedAlerts: string[],

  // Injectable for testing.
  now = new Date()
) {
  return alerts.find(
    (alert) =>
      !dismissedAlerts.includes(alert.key) &&
      alert.time.start.getTime() -
        (alert.type == "maintenance" ? 1 : 0) * timeUnit.day <=
        now.getTime() &&
      alert.time.end.getTime() > now.getTime() &&
      (alert.location.global ||
        alert.location.regions?.includes(account.region) ||
        alert.location.cells?.includes(account.cell))
  );
}

const dismissedAlertsStorageKey = "dismissedAlerts";

export function ScheduledAlerts() {
  const storage = localStorage;

  const { activeAccount } = useActiveAccount();

  const [dismissedAlerts, setDismissedAlerts] = useState(() =>
    tryParseJsonArray(storage.getItem(dismissedAlertsStorageKey) ?? "[]", [])
  );

  const dismissAlert = useCallback(
    function (alertKey: string) {
      const dismissed = [...dismissedAlerts, alertKey];
      setDismissedAlerts(dismissed);
      storage.setItem(dismissedAlertsStorageKey, JSON.stringify(dismissed));
    },
    [dismissedAlerts]
  );

  if (!activeAccount) {
    return null;
  }

  const activeAlertParams = findActiveAlertForAccount(
    scheduledAlerts,
    activeAccount,
    dismissedAlerts
  );

  switch (activeAlertParams?.type) {
    case "cta":
      return (
        <CTABanner
          params={activeAlertParams}
          onDismiss={() => dismissAlert(activeAlertParams.key)}
        />
      );
    case "maintenance":
      return (
        <MaintenanceBanner
          params={activeAlertParams}
          onDismiss={() => dismissAlert(activeAlertParams.key)}
        />
      );
    default:
      return null;
  }
}

export function CTABanner({
  params,
  onDismiss,
}: {
  params: AlertParams & { type: "cta" };
  onDismiss: () => void;
}) {
  return (
    <AlertBanner
      severity="info"
      sx={{ margin: 0, padding: 0, alignItems: "center" }}
    >
      <span>{params.message}</span>
      <Link
        sx={{
          cursor: "pointer",
          color: "inherit",
          textDecoration: "underline",
          marginLeft: "1.2ch",
        }}
        href={params.cta.url}
        target="_blank"
      >
        {params.cta.label}
        <Launch sx={{ fontSize: "1em", verticalAlign: "middle" }} />
      </Link>
      <Link
        sx={{
          cursor: "pointer",
          color: "inherit",
          textDecoration: "underline",
          marginLeft: "1.2ch",
        }}
        onClick={onDismiss}
      >
        Dismiss
      </Link>
    </AlertBanner>
  );
}

export function MaintenanceBanner({
  params: params,
  onDismiss,
}: {
  params: AlertParams & { type: "maintenance" };
  onDismiss: () => void;
}) {
  return (
    <AlertBanner
      severity="warning"
      sx={{ margin: 0, paddingX: 1, paddingY: 0, alignItems: "center" }}
    >
      <span>
        A maintenance window is scheduled to begin on{" "}
        <Timestamp timestamp={params.time.start} />
        {" and end on "}
        <Timestamp timestamp={params.time.end} />. {params.message}
      </span>
      <Link
        sx={{
          cursor: "pointer",
          color: "inherit",
          textDecoration: "underline",
          marginLeft: "1.2ch",
        }}
        onClick={onDismiss}
      >
        Dismiss
      </Link>
    </AlertBanner>
  );
}

function Timestamp({ timestamp }: { timestamp: Date }) {
  const { isoWithTimeZone, offset } = useMemo(() => {
    const isoWithTimeZone = moment(timestamp).toISOString(true);
    let offset =
      isoWithTimeZone.match(/(Z|[+-]\d\d:?(\d\d)?)$/)?.[0] ?? "local";
    offset = offset?.[0] == "Z" ? "UTC" : `UTC${offset}`;
    return { isoWithTimeZone, offset };
  }, [timestamp]);

  return (
    <Tooltip title={isoWithTimeZone}>
      <span style={{ textDecoration: "underline" }}>
        {moment(timestamp).format("D MMMM YYYY [at] h:mmA").trim()} ({offset})
      </span>
    </Tooltip>
  );
}

// Parse the given string as an array,
// returning the given defaultValue if something goes wrong.
function tryParseJsonArray(json: string, defaultValue: unknown[]) {
  try {
    const maybeArray = JSON.parse(json);
    if (Array.isArray(maybeArray)) {
      return maybeArray;
    }
    return defaultValue;
  } catch {
    return defaultValue;
  }
}
